import * as React from 'react';
import { Button } from '@material-ui/core';
import classnames from 'classnames';
import { observer } from 'mobx-react';

import { currency } from 'src/util/formatters';
import { Estimate } from 'src/api';
import { estimateContainer } from '../../../ioc';
import { EstimateStore, PresentationSettingsState } from '../../../stores/estimate.store';
import { hasMemberSavings, memberSavings } from 'src/estimate/util/member-savings';
import { ProposalOptionBar } from '../../proposal-option-bar/proposal-option-bar';
import styles from './estimate-card.less';

interface Props {
    estimate: Estimate;
    isSelected: boolean;
    showProposalOption?: boolean;
    financingEnabled?: boolean;
    financingPresentationSetting: PresentationSettingsState;
    popOver?: boolean;
}

const estimateStore = estimateContainer.get(EstimateStore);

export const EstimateCard = observer<React.SFC<Props>>(
    ({
        estimate,
        isSelected,
        showProposalOption,
        financingEnabled,
        financingPresentationSetting,
        popOver,
    }) => {
        const key = `${estimate.stateCode}_${estimate.total}`;
        const calculation = estimateStore.calculations.get(key);

        const { tenantId, buId, jobId, technicianId, customer, stateCode, total } = estimate;
        const customerEmail = estimateStore.customerEmail;
        const iframeRef = React.useRef<HTMLIFrameElement>(null);

        React.useEffect(() => {
            const handleMessage = (event: MessageEvent) => {
                if (event.data.type === 'openTerms' && iframeRef.current) {
                    iframeRef.current.style.setProperty('height', '700px', 'important');
                } else if (event.data.type === 'closeTerms' && iframeRef.current) {
                    iframeRef.current.style.removeProperty('height');
                }
            };
        
            window.addEventListener('message', handleMessage);
        
            return () => {
                window.removeEventListener('message', handleMessage);
            };
        }, []);

        return (
            <div
                className={classnames(styles.estimateCard, {
                    [styles.selected]: isSelected,
                    [styles.showProposalOption]: showProposalOption,
                })}
                onClick={() => estimateStore.gotoEstimate(estimate.id)}
            >
                {showProposalOption && (
                    <ProposalOptionBar
                        text={estimate.proposalTagName}
                        backgroundColor={estimate.proposalTagColor}
                        className={styles.proposalOptionBar}
                    />
                )}

                <span className={styles.estimateName}>{estimate.name}</span>
                {financingEnabled &&
                    calculation &&
                    calculation.eligibleForApplication &&
                    financingPresentationSetting !==
                        PresentationSettingsState.OnlyDisplayTotalPrice && (
                        <div
                            className={styles.estimateCalculation}
                            onClick={() => estimateStore.openCalculationCard(calculation)}
                        >
                            As Low As{' '}
                            {currency(
                                (calculation.promotionalMonthlyPayment ||
                                    calculation.defaultMonthlyPayment) ??
                                    0
                            )}
                            /mo
                        </div>
                    )}
                {financingPresentationSetting !==
                    PresentationSettingsState.OnlyDisplayMonthlyPrice && (
                    <div className={styles.pricing}>
                        <div className={styles.total}>
                            <div>Your Price</div>
                            {currency(estimate.total || 0)}
                        </div>
                        <div className={styles.savings}>
                            {hasMemberSavings(estimate) && (
                                <React.Fragment>
                                    <div>
                                        {estimate.isMember ? 'Member Savings' : 'Member Price'}
                                    </div>
                                    <span>{memberSavings(estimate)}</span>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                )}
                <div className="space-filler"></div>
                {!financingEnabled &&
                    financingPresentationSetting !==
                        PresentationSettingsState.OnlyDisplayTotalPrice && (
                        <div className={styles.container}>
                            <iframe
                                ref={iframeRef}
                                className={popOver ? styles.iframeOpen : styles.iframeClose}
                                src={`/financing/iframe/aslowas?tenantId=${tenantId}&estimateAmount=${total}&buId=${buId}&technicianId=${technicianId}&customerId=${
                                    customer?.id
                                }&jobId=${jobId}&estimateStateCode=${stateCode}&customerFirstName=${encodeURIComponent(
                                    customer?.firstName ?? ''
                                )}&customerLastName=${encodeURIComponent(
                                    customer?.lastName ?? ''
                                )}&customerEmail=${encodeURIComponent(
                                    customerEmail ?? ''
                                )}&calcKey=${estimate.id}&iframeMode=true`}
                            />
                        </div>
                    )}
                <Button
                    className={classnames('next-step-button', styles.button, {
                        [styles.notSelected]: !isSelected,
                    })}
                    variant="contained"
                    color="primary"
                    disabled={isSelected && estimate.isAccepted}
                    onClick={
                        isSelected
                            ? estimateStore.gotoTerms
                            : () => estimateStore.gotoEstimate(estimate.id)
                    }
                >
                    {estimate.isAccepted ? 'Accepted' : isSelected ? 'Accept Estimate' : 'View'}
                </Button>
            </div>
        );
    }
);
